<template>
  <div>
    <div v-if="errored" class="errored">Une erreur s'est produite 😥</div>

    <div v-else-if="loaded" id="side-bar-projects" :class="'resume ' + person.lang">
      <div class="banner">
        <h1>{{ person.name.first }} {{ person.name.last }}</h1>
        <div class="position">
          {{ person.position }}<span v-if="person.birth.year"> — {{ lang.born }} {{ person.birth.year }}</span>
        </div>
      </div>

      <div class="container">
        <div class="content-left">
          <section v-if="person.picture" class="profile"><img :src="person.picture" /></section>

          <section v-if="person.about" class="about description">{{ person.about }}</section>

          <section class="contact">
            <h2>{{ lang.contact }}</h2>
            <ul>
              <li v-if="person.contact.street">
                <i class="fa fa-home"></i>
                {{ person.contact.street }}, {{ person.contact.city }}
              </li>
              <li>
                <i class="fa fa-envelope"></i>
                <a :href="contactLinks.email">{{ person.contact.email }}</a>
              </li>
              <li>
                <i class="fa fa-phone"></i>
                <a :href="'tel:' + person.contact.phone">{{ person.contact.phone }}</a>
              </li>
              <li v-if="person.contact.website">
                <i class="fa fa-globe"></i>
                <a :href="person.contact.website" target="_blank">{{ person.contact.website }}</a>
              </li>
              <li v-if="person.contact.github">
                <i class="fa fa-github"></i>
                <a :href="contactLinks.github" target="_blank">{{ person.contact.github }}</a>
              </li>
            </ul>
          </section>

          <section class="skills">
            <h2>{{ lang.skills }}</h2>
            <ul>
              <li v-for="skill in person.skills">
                <span>{{ skill.name }}</span>
                <span class="skill-bar">
                  <span :style="'width: ' + skill.level + '%'" class="level"></span>
                </span>
              </li>
            </ul>
          </section>
        </div>

        <div class="content-right">
          <section v-if="person.experience.length" class="experience">
            <h2>{{ lang.experience }}</h2>
            <div v-for="experience in person.experience" class="item">
              <h3>
                {{ experience.position }}, {{ experience.company }}<br />
                <span class="timeperiod">{{ experience.timeperiod_from }} - {{ experience.timeperiod_to || lang.today }}</span>
              </h3>
              <div v-if="experience.description" class="description">{{ experience.description }}</div>
            </div>
          </section>

          <section v-if="person.education.length" class="education">
            <h2>{{ lang.education }}</h2>
            <div v-for="education in person.education" class="item">
              <h3>
                {{ education.degree_role }}, {{ education.degree_structure }}<br />
                <span class="timeperiod">{{ education.timeperiod }}</span>
              </h3>
              <div v-if="education.description" class="description">{{ education.description }}</div>
            </div>
          </section>

          <section v-if="person.projects.length" class="projects">
            <h2>{{ lang.projects }}</h2>
            <div v-for="project in person.projects" class="item">
              <h3>
                {{ project.name }}<br />
                <span class="timeperiod">{{ project.timeperiod_from }} - {{ project.timeperiod_to }}</span>
                <div class="meta">{{ project.platform }}, {{ project.url }}</div>
              </h3>
              <div v-if="project.description" class="description">{{ project.description }}</div>
            </div>
          </section>
        </div>
      </div>
    </div>
    <div v-else class="loading">Chargement en cours ⏰</div>
  </div>
</template>

<script>
import Vue from 'vue';
import { getVueOptions } from './options';

const name = 'side-bar-projects';
export default Vue.component(name, getVueOptions(name));
</script>

<style lang="less" scoped>
@page side-bar-projects {
  margin: 2cm 0;
}

@page side-bar-projects:first {
  margin-top: 0;
}

@primary-color: #4b5563;
@secondary-color: #f43f5e;
@normal: 11pt;
@big: 32pt;
@small: 10pt;

#side-bar-projects {
  position: relative;
  color: @primary-color;
  font-family: 'Brown', sans-serif;
  font-weight: 300;
  font-size: @normal;
  line-height: 17pt;
  page: side-bar-projects;
  .ar& {
    direction: rtl;
    font-family: 'Noto Sans Arabic', 'Brown', sans-serif;
    font-weight: 400;
  }

  .banner {
    margin-bottom: 1cm;
    color: @secondary-color;
    text-align: center;
    margin-top: 2cm;

    h1 {
      text-transform: uppercase;
      font-size: @big;
      line-height: 1.3;
      margin: 0;
      .ar& {
        font-weight: 700;
      }
    }

    .position {
      text-transform: uppercase;
      font-size: @small;
    }
  }

  .container {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));

    .content-left {
      grid-column-start: 1;
      grid-column-end: 6;
      padding-inline-start: 2cm;
      padding-inline-end: 0.5cm;
    }

    .content-right {
      grid-column-start: 6;
      grid-column-end: 13;
      padding-inline-start: 0.5cm;
      padding-inline-end: 2cm;
    }
  }

  section {
    margin: 1cm 0;
  }

  .profile img {
    width: 100%;
    height: auto;
    vertical-align: middle;
  }

  .description {
    white-space: pre-line;
  }

  .timeperiod {
    text-transform: capitalize;
  }

  .item:not(:last-child) {
    margin-bottom: 0.3cm;
  }

  h2 {
    font-size: @normal;
    text-transform: uppercase;
    color: @secondary-color;
    margin-top: 0;
    margin-bottom: 1cm;
    position: relative;
    width: fit-content;
    letter-spacing: 3px;
    .ar& {
      letter-spacing: 0;
      font-weight: 700;
    }

    &:after {
      width: 50%;
      height: 2px;
      background-color: @secondary-color;
      content: '';
      position: absolute;
      inset-inline-start: 0;
      bottom: -0.5cm;
    }
  }

  h3 {
    font-size: @normal;
    text-transform: uppercase;
    margin: 0;
    .ar& {
      font-weight: 700;
    }
  }

  .contact ul {
    margin: 0;
    padding: 0;
    list-style: none;

    a {
      color: @primary-color;
      text-decoration: none;
    }
  }

  .skills {
    page-break-inside: avoid;
    break-inside: avoid;

    ul {
      margin: 0;
      padding: 0;
      list-style: none;
    }
  }
}
</style>
